import { createContext, useEffect, useReducer } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { MatxLoading } from "app/components"; // Loading spinner component

// Initial State
const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
};

// Reducer Function
const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isInitialized: true,
        isAuthenticated,
        user,
      };
    }
    case "LOGIN": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    default:
      return state;
  }
};

// Create Context
const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => { },
});

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Define clearAuthData at the component level
  const clearAuthData = () => {
    // Get all keys from localStorage
    const keys = Object.keys(localStorage);

    // Define patterns to match auth-related items
    const authPatterns = [
      /jwt/i,                    // Any JWT related keys
      /token/i,                  // Any token related keys
      /session/i,                // Any session related keys
      /teacher/i,                // Any teacher related keys
      /student/i,                // Any student related keys
      /^id$/i,                   // Simple id key
      /2024-2025/,              // Academic year pattern
      /T\d{3}/                  // Teacher ID pattern (e.g., T101)
    ];

    // Remove items matching any of our patterns
    keys.forEach(key => {
      if (authPatterns.some(pattern => pattern.test(key))) {
        localStorage.removeItem(key);
      }
    });

    // Also remove specific known keys
    const specificKeys = [
      'session_id1',
      'session_name2024-2025',
      'teacherIdT101',
      'jwtToken',
      'teacherId',
      'Token',
      'session_id',
      'session_name',
      'studentId',
      'userId',        // Added userId
      'userRole',      // Added userRole
      'role'
    ];

    specificKeys.forEach(key => localStorage.removeItem(key));

    // Final clear as a safety net
    localStorage.clear();
  };

  // Initialize Authentication State
  useEffect(() => {
    const initialize = async () => {
      const token = localStorage.getItem("jwtToken");

      if (!token) {
        dispatch({ type: "INIT", payload: { isAuthenticated: false, user: null } });
        return;
      }

      try {
        // Decode JWT and check expiration
        const decodedToken = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (decodedToken.exp < currentTime) {
          clearAuthData();
          dispatch({ type: "INIT", payload: { isAuthenticated: false, user: null } });
          return;
        }

        // Fetch user profile from the backend
        const response = await axios.get("https://www.api-berhampore.dbsmerp.in/api/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Store user data and id in localStorage
        localStorage.setItem("id", response.data.user?.id || response.data.teacher?.id);

        dispatch({
          type: "INIT",
          payload: { isAuthenticated: true, user: response.data.user || response.data.teacher },
        });
      } catch (error) {
        console.error("Initialization failed:", error);
        clearAuthData();
        dispatch({ type: "INIT", payload: { isAuthenticated: false, user: null } });
      }
    };

    initialize();
  }, []);

  // Login Function
  const login = async ({ email, password, admission_no, role }) => {
    try {
      let endpoint;

      // Determine the correct endpoint based on role
      if (role === "administrator") {
        endpoint = "login"; // Admin login
      } else if (role === "teacher") {
        endpoint = "teacher/login"; // Teacher login
      } else if (role === "librarian") {
        endpoint = "librarian/login"; // Librarian login
      } else if (role === "receptionist") {
        endpoint = "receptionist/login";// receptionist login
      } else if (role === "student") {
        endpoint = "student_login"; // Student login
      } else {
        throw new Error("Invalid role specified"); // Handle unexpected roles
      }

      // Prepare payload based on role
      const payload =
        role === "student"
          ? { admission_no, password } // Student payload
          : { email, password }; // Admin/Teacher payload

      const response = await axios.post(`https://www.api-berhampore.dbsmerp.in/${endpoint}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const { token, user, teacher, student } = response.data;

      // Store token and role in localStorage
      localStorage.setItem("jwtToken", token);
      localStorage.setItem("userRole", role); // Store the role

      // Store the appropriate user identifier in localStorage
      if (teacher) {
        localStorage.setItem("teacherId", teacher.id);
      } else if (student) {
        localStorage.setItem("studentId", student.id);
      } else if (user) {
        localStorage.setItem("userId", user.id); // For admin/librarian/receptionist
      }

      // Dispatch login action with user data
      dispatch({ type: "LOGIN", payload: { user: user || teacher || student } });

      return { token, user: user || teacher || student };
    } catch (error) {
      console.error("Login failed:", error.response?.data || error.message);
      throw new Error(error.response?.data?.message || "Login failed");
    }
  };
  // Logout Function
  const logout = async () => {
    try {
      // Make a call to the backend logout route
      const response = await axios.get("/logout");

      // Clear all auth-related data
      clearAuthData();

      // Dispatch the logout action
      dispatch({ type: "LOGOUT" });

      // Add a small delay before redirect to ensure cleanup completes
      setTimeout(() => {
        window.location.href = "/login";
      }, 100);

    } catch (error) {
      console.error("An error occurred during logout:", error);
      // Even if the backend call fails, we should still clear storage and logout
      clearAuthData();
      dispatch({ type: "LOGOUT" });

      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
    }
  };

  // Render Loading Spinner until initialization is complete
  if (!state.isInitialized) return <MatxLoading />;

  // Provide authentication context to children
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;